import type { EdgeScrollHandle } from "@/hooks/useCanvasHandlers";
import { shouldHighlightHoveredHex, useCanvasHandlers } from "@/hooks/useCanvasHandlers";
import { useEdgeScroll } from "@/hooks/useEdgeScroll";
import { useKeyboardNav } from "@/hooks/useKeyboardNav";
import { useStoreRefs } from "@/hooks/useStoreRefs";
import {
  applyZoomAtScreenPoint,
  clampCamera,
  clampZoomLevelIndex,
  getClosestZoomLevelIndex,
  MAP_ZOOM_LEVELS,
  screenToWorld,
} from "@/lib/camera";
import { getNearestTextLabelFontSize } from "@/lib/textLabelSizes";
import { getBroadcastMapChange } from "@/state/realtimeBroadcast";
import {
  createScatterPreviewPoints,
  drawHexes,
  drawPlacementPreview,
  type PlacementPreview,
} from "@/lib/drawHexes";
import {
  drawTextLabels,
  getRotationHandleWorldPos,
  hitTestRotationHandle,
  hitTestTextLabels,
} from "@/lib/drawTextLabels";
import { usePrefetchMapNoteEntries } from "@/lib/queries/mapNoteEntryQueries";
import { useMapNoteListQuery, useUpdateMapNoteMutation } from "@/lib/queries/mapNoteQueries";
import { useToast } from "@/components/ui/ToastProvider";
import { useEditorStore } from "@/state/useEditorStore";
import { useMapStore } from "@/state/useMapStore";
import { useMapNoteStore } from "@/state/useMapNoteStore";
import { useTextLabelStore } from "@/state/useTextLabelStore";
import type { HexData, MapDecoration, MapNote, MapNotePinColor, MapTextLabel } from "@west-marches/shared";
import {
  getCatalogDecorationById, getCoastOverlaysForHex, getRoadOverlaysForEdges, hexKey, hexToPixel, pixelToHex,
  MAP_NOTE_PIN_OVERLAY_ID,
  requestCoastOverlayImages, requestDecorationImages, requestRoadOverlayImages,
  requestTileImages, requestTitleBannerImages, resolveTileId
} from "@west-marches/shared";
import { DEFAULT_MAP_CAMERA, getVisibleHexKeys } from "@/lib/visibleHexes";
import type { MapNotePinDrawInfo } from "@/lib/drawHexes";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EyedropperOverlay } from "./EyedropperOverlay";
import { MapNoteMoveBanner } from "./MapNoteMoveBanner";
import { MapNotePopover } from "./MapNotePopover";
import { MapNoteToolPanel } from "./MapNoteToolPanel";
import { TextLabelEditor } from "./TextLabelEditor";
import { TextStyleToolbar } from "./TextStyleToolbar";

const eraserCursorSvg = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
    <g transform="translate(4 3) rotate(-18 12 13)">
      <rect x="7" y="3" width="12" height="18" rx="2.5" fill="#f7d7a8" stroke="#3f2a1c" stroke-width="1.5"/>
      <rect x="7" y="15" width="12" height="6" rx="0 0 2.5 2.5" fill="#f3a6b8" stroke="#3f2a1c" stroke-width="1.5"/>
      <line x1="7" y1="15" x2="19" y2="15" stroke="#3f2a1c" stroke-width="1.5"/>
    </g>
  </svg>`,
);

function getVisibleTileIds(
  hexes: Record<string, HexData>,
  visibleKeys: string[],
): string[] {
  const tileIds = new Set<string>();

  for (const key of visibleKeys) {
    const hex = hexes[key];
    const tileId = resolveTileId({
      tileId: hex.tileId,
      tileFilename: hex.tileFilename,
      tileSetId: hex.tileSetId,
    });
    if (tileId) {
      tileIds.add(tileId);
    }
  }

  return Array.from(tileIds);
}

function getVisibleOverlayFilenames(
  hexes: Record<string, HexData>,
  visibleKeys: string[],
): { coast: string[]; road: string[] } {
  const coast = new Set<string>();
  const road = new Set<string>();

  for (const key of visibleKeys) {
    const hex = hexes[key];
    if (!hex) {
      continue;
    }

    for (const overlay of getCoastOverlaysForHex(hex.q, hex.r, hexes)) {
      coast.add(overlay.filename);
    }

    if (!hex.roadEdges || hex.roadEdges.length === 0) {
      continue;
    }

    for (const overlay of getRoadOverlaysForEdges(hex.roadEdges, key)) {
      road.add(overlay.filename);
    }
  }

  return {
    coast: Array.from(coast),
    road: Array.from(road),
  };
}

function getVisibleDecorations(
  decorations: Record<string, MapDecoration>,
  visibleHexKeys: string[],
): Array<{ id: string; url: string }> {
  const visibleHexKeySet = new Set(visibleHexKeys);
  const visibleDecorations: Array<{ id: string; url: string }> = [];

  for (const decoration of Object.values(decorations)) {
    const key = `${decoration.q},${decoration.r}`;
    if (!visibleHexKeySet.has(key)) {
      continue;
    }
    const catalogDecoration = getCatalogDecorationById(decoration.decorationId);
    if (!catalogDecoration) {
      continue;
    }
    visibleDecorations.push({
      id: decoration.decorationId,
      url: catalogDecoration.url,
    });
  }

  return visibleDecorations;
}

export function getPlacementPreview(
  mapState: {
    hexes: Record<string, HexData>;
  },
  pointerWorld: { x: number; y: number; inside: boolean },
  selection: {
    activeTool: string;
    selectedTileSetId: string | null;
    selectedBrushEntry: ReturnType<typeof useEditorStore.getState>["selectedBrushEntry"];
    selectedDecorationId: string | null;
    decorationMode: ReturnType<typeof useEditorStore.getState>["decorationMode"];
    decorationScatterRadius: number;
    decorationScatterDensity: number;
    pendingPinColor?: MapNotePinColor;
    pendingHidden?: boolean;
  },
): PlacementPreview | null {
  if (!pointerWorld.inside) {
    return null;
  }

  const hex = pixelToHex(pointerWorld.x, pointerWorld.y);
  const key = hexKey(hex);
  if (!mapState.hexes[key]) {
    return null;
  }

  if (
    selection.selectedDecorationId &&
    (selection.decorationMode === "single" || selection.decorationMode === "brush")
  ) {
    return {
      kind: "decoration",
      decorationId: selection.selectedDecorationId,
      x: pointerWorld.x,
      y: pointerWorld.y,
      radius: selection.decorationMode === "brush" ? selection.decorationScatterRadius : 0,
      points:
        selection.decorationMode === "brush"
          ? createScatterPreviewPoints(
              selection.decorationScatterRadius,
              selection.decorationScatterDensity,
            )
          : [{ x: 0, y: 0 }],
    };
  }

  if (selection.selectedDecorationId && selection.decorationMode === "erase") {
    return {
      kind: "erase",
      x: pointerWorld.x,
      y: pointerWorld.y,
      radius: selection.decorationScatterRadius,
    };
  }

  if (selection.activeTool === "map-note" && selection.pendingPinColor) {
    return {
      kind: "map-note-pin",
      color: selection.pendingPinColor,
      x: pointerWorld.x,
      y: pointerWorld.y,
      hidden: selection.pendingHidden,
    };
  }

  return null;
}

export function getCanvasCursorStyle(
  selection: {
    activeTool: ReturnType<typeof useEditorStore.getState>["activeTool"];
    selectedTileSetId: string | null;
    selectedDecorationId: string | null;
    decorationMode: ReturnType<typeof useEditorStore.getState>["decorationMode"];
    decorationScatterRadius?: number;
    decorationScatterDensity?: number;
  },
  options: {
    spaceHeld: boolean;
    customCursor: string;
    brushCursor: string;
    eraserCursor: string;
    textTransformHandleHovered?: boolean;
    textTransformActive?: boolean;
  },
): string {
  if (options.spaceHeld) {
    return "grab";
  }

  if (options.textTransformActive) {
    return "grabbing";
  }

  if (options.textTransformHandleHovered) {
    return "grab";
  }

  if (selection.activeTool === "eyedropper") {
    return "none";
  }

  if (
    selection.selectedDecorationId &&
    (selection.decorationMode === "single" || selection.decorationMode === "brush")
  ) {
    return "none";
  }

  if (selection.selectedDecorationId && selection.decorationMode === "erase") {
    return options.eraserCursor;
  }

  if (selection.activeTool === "text") {
    return "text";
  }

  if (selection.activeTool === "map-note") {
    return "none";
  }

  if (selection.activeTool === "road") {
    return "crosshair";
  }

  if (
    (selection.activeTool === "tile-brush" || selection.activeTool === "decoration-brush") &&
    selection.selectedTileSetId
  ) {
    return options.brushCursor;
  }

  return options.customCursor;
}

const DEFAULT_CAMERA = DEFAULT_MAP_CAMERA;
const ZOOM_WHEEL_STEP_THRESHOLD = 48;
let savedCamera: { x: number; y: number; zoom: number } | null = null;

export default function MapCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cameraRef = useRef(savedCamera ?? { ...DEFAULT_CAMERA });
  const hoveredHexRef = useRef<string | null>(null);
  const pointerWorldRef = useRef({ x: 0, y: 0, inside: false });
  const edgeScrollRef = useRef<EdgeScrollHandle | null>(null);
  const spaceHeldRef = useRef(false);
  const latestRef = useStoreRefs();
  const textTransformHandleHoveredRef = useRef(false);
  const wheelZoomAccumulatorRef = useRef(0);
  const [zoomLevelIndex, setZoomLevelIndex] = useState(() =>
    getClosestZoomLevelIndex(cameraRef.current.zoom),
  );

  const activeCampaignId = useEditorStore((s) => s.activeCampaignId);
  const canEdit = useEditorStore((s) => s.canEdit);
  const viewAs = useEditorStore((s) => s.viewAs);
  const setActiveTool = useEditorStore((s) => s.setActiveTool);
  const setEditorMode = useEditorStore((s) => s.setEditorMode);
  const isPreviewingAsPlayer = viewAs === "player";
  const { data: mapNotes } = useMapNoteListQuery(activeCampaignId, true, {
    asPlayer: isPreviewingAsPlayer,
  });
  usePrefetchMapNoteEntries(activeCampaignId, mapNotes, "player", { asPlayer: isPreviewingAsPlayer });
  const mapNoteByHexKey = useMemo(() => {
    const map = new Map<string, MapNote>();
    if (mapNotes) {
      for (const mapNote of mapNotes) {
        map.set(`${mapNote.q},${mapNote.r}`, mapNote);
      }
    }
    return map;
  }, [mapNotes]);
  const mapNotePinByHexKey = useMemo(() => {
    const map = new Map<string, MapNotePinDrawInfo>();
    for (const [key, mapNote] of mapNoteByHexKey) {
      map.set(key, {
        id: mapNote.id,
        color: mapNote.color,
        offsetX: mapNote.offsetX ?? 0,
        offsetY: mapNote.offsetY ?? 0,
        hidden: mapNote.hidden,
      });
    }
    return map;
  }, [mapNoteByHexKey]);
  const mapNoteByHexKeyRef = useRef(mapNoteByHexKey);
  mapNoteByHexKeyRef.current = mapNoteByHexKey;
  const mapNotePinByHexKeyRef = useRef(mapNotePinByHexKey);
  mapNotePinByHexKeyRef.current = mapNotePinByHexKey;
  const pendingPinColor = useMapNoteStore((s) => s.pendingPinColor);
  const pendingPinColorRef = useRef(pendingPinColor);
  pendingPinColorRef.current = pendingPinColor;
  const pendingHidden = useMapNoteStore((s) => s.pendingHidden);
  const pendingHiddenRef = useRef(pendingHidden);
  pendingHiddenRef.current = pendingHidden;

  const [cursorPreview, setCursorPreview] = useState({
    clientX: 0,
    clientY: 0,
    visible: false,
    hoveredTileSetId: null as string | null,
  });

  const customCursor = "url('/ui/cursor.png') 6 2, auto";
  const brushCursor = "url('/cursor/brush-cursor.png') 5 2, crosshair";
  const eraserCursor = `url("data:image/svg+xml;charset=utf-8,${eraserCursorSvg}") 10 24, crosshair`;
  const updateCursor = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const textLabelState = useTextLabelStore.getState();
    canvas.style.cursor = getCanvasCursorStyle(
      {
        activeTool: latestRef.current.activeTool,
        selectedTileSetId: latestRef.current.selectedTileSetId,
        selectedDecorationId: latestRef.current.selectedDecorationId,
        decorationMode: latestRef.current.decorationMode,
      },
      {
        spaceHeld: spaceHeldRef.current,
        customCursor,
        brushCursor,
        eraserCursor,
        textTransformHandleHovered: textTransformHandleHoveredRef.current,
        textTransformActive: textLabelState.rotateState !== null,
      },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setTextTransformHandleHovered = useCallback((hovered: boolean) => {
    if (textTransformHandleHoveredRef.current === hovered) return;
    textTransformHandleHoveredRef.current = hovered;
    updateCursor();
  }, [updateCursor]);

  const mapState = useMapStore((s) => s.mapState);

  const draw = useCallback(function drawCanvas() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { width, height } = canvas;
    const cam = cameraRef.current;
    const ms = latestRef.current.mapState;

    // Constrain panning so the viewport edge never escapes the playable map.
    // Mutates cam in place; every pan/zoom path calls draw() after touching
    // the camera, so this single clamp covers wheel, edge-scroll, keyboard,
    // space-drag, and zoom.
    clampCamera(cam, canvas, ms.bounds);

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = "#0d1117";
    ctx.fillRect(0, 0, width, height);

    ctx.setTransform(
      cam.zoom,
      0,
      0,
      cam.zoom,
      width / 2 + cam.x,
      height / 2 + cam.y,
    );

    const sortedKeys = Object.keys(ms.hexes).sort((a, b) => {
      const ha = ms.hexes[a];
      const hb = ms.hexes[b];
      const pa = hexToPixel({ q: ha.q, r: ha.r });
      const pb = hexToPixel({ q: hb.q, r: hb.r });
      return pa.y - pb.y;
    });

    const visibleKeys = getVisibleHexKeys(ms.hexes, sortedKeys, cam, width, height);
    const visibleTileIds = getVisibleTileIds(ms.hexes, visibleKeys);
    const visibleOverlays = getVisibleOverlayFilenames(ms.hexes, visibleKeys);
    const visibleDecorations = getVisibleDecorations(ms.decorations, visibleKeys);
    const placementPreview = getPlacementPreview(ms, pointerWorldRef.current, {
      activeTool: latestRef.current.activeTool,
      selectedTileSetId: latestRef.current.selectedTileSetId,
      selectedBrushEntry: latestRef.current.selectedBrushEntry,
      selectedDecorationId: latestRef.current.selectedDecorationId,
      decorationMode: latestRef.current.decorationMode,
      decorationScatterRadius: latestRef.current.decorationScatterRadius,
      decorationScatterDensity: latestRef.current.decorationScatterDensity,
      pendingPinColor: pendingPinColorRef.current,
      pendingHidden: pendingHiddenRef.current,
    });

    requestTileImages(visibleTileIds, drawCanvas);
    requestCoastOverlayImages(visibleOverlays.coast, drawCanvas);
    requestRoadOverlayImages(visibleOverlays.road, drawCanvas);
    requestDecorationImages(visibleDecorations, drawCanvas);
    if (placementPreview?.kind === "decoration") {
      const catalogDecoration = getCatalogDecorationById(placementPreview.decorationId);
      if (catalogDecoration) {
        requestDecorationImages(
          [{ id: placementPreview.decorationId, url: catalogDecoration.url }],
          drawCanvas,
        );
      }
    }
    if (placementPreview?.kind === "map-note-pin") {
      const overlayId = MAP_NOTE_PIN_OVERLAY_ID[placementPreview.color];
      const catalog = overlayId ? getCatalogDecorationById(overlayId) : null;
      if (catalog) {
        requestDecorationImages([{ id: overlayId, url: catalog.url }], drawCanvas);
      }
    }

    if (mapNotePinByHexKeyRef.current.size > 0) {
      const pinAssetIds = new Set<string>();
      for (const pin of mapNotePinByHexKeyRef.current.values()) {
        pinAssetIds.add(MAP_NOTE_PIN_OVERLAY_ID[pin.color]);
      }
      const pinDecorationRequests: Array<{ id: string; url: string }> = [];
      for (const overlayId of pinAssetIds) {
        const catalog = getCatalogDecorationById(overlayId);
        if (catalog) {
          pinDecorationRequests.push({ id: overlayId, url: catalog.url });
        }
      }
      if (pinDecorationRequests.length > 0) {
        requestDecorationImages(pinDecorationRequests, drawCanvas);
      }
    }

    drawHexes(
      ctx,
      ms.hexes,
      ms.decorations,
      sortedKeys,
      shouldHighlightHoveredHex(latestRef.current.activeTool) ? hoveredHexRef.current : null,
      mapNotePinByHexKeyRef.current,
      { dimmedMapNoteId: movingMapNoteIdRef.current },
    );
    drawPlacementPreview(ctx, placementPreview);

    const textLabelState = useTextLabelStore.getState();
    const hasBannerLabel = Object.values(ms.textLabels).some((label) => label.banner);
    if (hasBannerLabel || textLabelState.editingLabelId !== null) {
      requestTitleBannerImages(drawCanvas);
    }
    drawTextLabels(
      ctx,
      ms.textLabels,
      textLabelState.editingLabelId,
      textLabelState.selectedLabelId,
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setZoomLevel = useCallback(
    (nextIndex: number, origin?: { sx: number; sy: number }) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const clampedIndex = clampZoomLevelIndex(nextIndex);
      const nextZoom = MAP_ZOOM_LEVELS[clampedIndex];
      const cam = cameraRef.current;
      const currentIndex = getClosestZoomLevelIndex(cam.zoom);

      if (clampedIndex === currentIndex) {
        setZoomLevelIndex(currentIndex);
        return;
      }

      applyZoomAtScreenPoint(
        canvas,
        cam,
        nextZoom,
        origin?.sx ?? canvas.width / 2,
        origin?.sy ?? canvas.height / 2,
      );
      setZoomLevelIndex(clampedIndex);
      draw();
    },
    [draw],
  );

  const stepZoomLevel = useCallback(
    (direction: -1 | 1, origin?: { sx: number; sy: number }) => {
      const currentIndex = getClosestZoomLevelIndex(cameraRef.current.zoom);
      const nextIndex = clampZoomLevelIndex(currentIndex + direction);
      setZoomLevel(nextIndex, origin);
    },
    [setZoomLevel],
  );

  const showPopover = useMapNoteStore((s) => s.showPopover);
  const hidePopover = useMapNoteStore((s) => s.hidePopover);
  const scheduleHide = useMapNoteStore((s) => s.scheduleHide);
  const startEditing = useMapNoteStore((s) => s.startEditing);
  const showPopoverRef = useRef(showPopover);
  showPopoverRef.current = showPopover;
  const hidePopoverRef = useRef(hidePopover);
  hidePopoverRef.current = hidePopover;
  const scheduleHideRef = useRef(scheduleHide);
  scheduleHideRef.current = scheduleHide;
  const startEditingRef = useRef(startEditing);
  startEditingRef.current = startEditing;

  const movingMapNoteId = useMapNoteStore((s) => s.movingMapNoteId);
  const stopMoving = useMapNoteStore((s) => s.stopMoving);
  const movingMapNoteIdRef = useRef(movingMapNoteId);
  movingMapNoteIdRef.current = movingMapNoteId;
  const stopMovingRef = useRef(stopMoving);
  stopMovingRef.current = stopMoving;
  const updateMapNoteMutation = useUpdateMapNoteMutation(activeCampaignId);
  const updateMapNoteMutationRef = useRef(updateMapNoteMutation);
  updateMapNoteMutationRef.current = updateMapNoteMutation;
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const handlers = useCanvasHandlers({
    canvasRef,
    cameraRef,
    latestRef,
    hoveredHexRef,
    pointerWorldRef,
    spaceHeldRef,
    edgeScrollRef,
    draw,
    updateCursor,
    setCursorPreview,
  });

  const handleMouseMoveWithMapNote = useCallback((e: React.MouseEvent) => {
    const textStore = useTextLabelStore.getState();

    if (textStore.rotateState && textStore.selectedLabelId) {
      setTextTransformHandleHovered(false);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const { wx, wy } = screenToWorld(canvas, cameraRef.current, e.clientX, e.clientY);
      const ms = latestRef.current.mapState;
      const label = ms.textLabels[textStore.selectedLabelId];
      if (label) {
        const rs = textStore.rotateState;
        const angle = Math.atan2(wy - label.y, wx - label.x);
        const distance = Math.hypot(wx - label.x, wy - label.y);
        const snapStep = Math.PI / 16; // 5° steps
        const rotation = e.shiftKey ? angle : Math.round(angle / snapStep) * snapStep;
        const scaledFontSize = Math.round(
          rs.startFontSize * (distance / Math.max(rs.startHandleDistance, 1)),
        );
        const fontSize = e.shiftKey
          ? Math.max(scaledFontSize, 1)
          : getNearestTextLabelFontSize(scaledFontSize);

        useMapStore.getState().applyMapChanges({
          textLabels: { [label.id]: { ...label, rotation, fontSize } },
        });
        draw();
      }
      return;
    }

    if (textStore.dragState && textStore.selectedLabelId) {
      setTextTransformHandleHovered(false);
      const canvas = canvasRef.current;
      if (!canvas) return;
      const { wx, wy } = screenToWorld(canvas, cameraRef.current, e.clientX, e.clientY);
      const ds = textStore.dragState;
      const dx = wx - ds.startX;
      const dy = wy - ds.startY;
      const ms = latestRef.current.mapState;
      const label = ms.textLabels[textStore.selectedLabelId];
      if (label) {
        const updated: MapTextLabel = {
          ...label,
          x: ds.labelStartX + dx,
          y: ds.labelStartY + dy,
        };
        useMapStore.getState().applyMapChanges({
          textLabels: { [label.id]: updated },
        });
        draw();
      }
      return;
    }

    if (textStore.editingLabelId) {
      edgeScrollRef.current?.stopEdgeScroll();
      return;
    }

    handlers.handleMouseMove(e);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const { wx, wy } = screenToWorld(canvas, cameraRef.current, e.clientX, e.clientY);
    if (textStore.selectedLabelId) {
      const selectedLabel = latestRef.current.mapState.textLabels[textStore.selectedLabelId];
      if (selectedLabel) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        const onHandle = hitTestRotationHandle(ctx, selectedLabel, wx, wy);
        ctx.restore();
        setTextTransformHandleHovered(onHandle);
      } else {
        setTextTransformHandleHovered(false);
      }
    } else {
      setTextTransformHandleHovered(false);
    }

    const hex = pixelToHex(wx, wy);
    const key = hexKey(hex);
    const mapNote = mapNoteByHexKeyRef.current.get(key);

    if (mapNote) {
      const cam = cameraRef.current;
      const center = hexToPixel(hex);
      const sx = canvas.width / 2 + cam.x + center.x * cam.zoom;
      const sy = canvas.height / 2 + cam.y + center.y * cam.zoom;
      showPopoverRef.current(mapNote.id, sx, sy);
    } else {
      scheduleHideRef.current();
    }
  }, [handlers, latestRef, draw, setTextTransformHandleHovered]);

  const lastTextClickRef = useRef<{ time: number; labelId: string | null }>({
    time: 0,
    labelId: null,
  });

  const activateEnrichPointer = useCallback(() => {
    setEditorMode("enrich");
    setActiveTool("pointer");
  }, [setActiveTool, setEditorMode]);

  const handleMouseDownWithMapNote = useCallback((e: React.MouseEvent) => {
    const tool = latestRef.current.activeTool;

    const movingId = movingMapNoteIdRef.current;
    if (movingId !== null && e.button !== 1) {
      // Right-click cancels move mode without moving.
      if (e.button === 2) {
        e.preventDefault();
        stopMovingRef.current();
        return;
      }
      if (e.button === 0) {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const { wx, wy } = screenToWorld(canvas, cameraRef.current, e.clientX, e.clientY);
        const hex = pixelToHex(wx, wy);
        const key = hexKey(hex);
        const ms = latestRef.current.mapState;
        if (!ms.hexes[key]) {
          // Click on empty space: stay in move mode.
          return;
        }
        const center = hexToPixel(hex);
        const offsetX = wx - center.x;
        const offsetY = wy - center.y;
        updateMapNoteMutationRef.current.mutate(
          {
            mapNoteId: movingId,
            input: { q: hex.q, r: hex.r, offsetX, offsetY },
          },
          {
            onSuccess: () => stopMovingRef.current(),
            onError: (error) => {
              const message = error instanceof Error ? error.message : "Unknown error";
              if (message.includes("already exists")) {
                toastRef.current.error("A map note already exists on this tile.");
              } else {
                toastRef.current.error(`Couldn't move pin: ${message}`);
              }
              stopMovingRef.current();
            },
          },
        );
        return;
      }
    }

    if (tool === "text" && e.button === 0) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const { wx, wy } = screenToWorld(canvas, cameraRef.current, e.clientX, e.clientY);
      const ms = latestRef.current.mapState;

      const textStore = useTextLabelStore.getState();

      if (textStore.selectedLabelId) {
        const selectedLabel = ms.textLabels[textStore.selectedLabelId];
        if (selectedLabel) {
          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          const onHandle = hitTestRotationHandle(ctx, selectedLabel, wx, wy);
          ctx.restore();
          if (onHandle) {
            const handle = getRotationHandleWorldPos(ctx, selectedLabel);
            const startHandleDistance = Math.hypot(
              handle.x - selectedLabel.x,
              handle.y - selectedLabel.y,
            );
            setTextTransformHandleHovered(false);
            textStore.startRotate(
              selectedLabel.rotation ?? 0,
              selectedLabel.fontSize,
              startHandleDistance,
            );
            updateCursor();
            return;
          }
        }
      }

      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const hit = hitTestTextLabels(ctx, ms.textLabels, wx, wy);
      ctx.restore();

      const now = Date.now();
      const lastClick = lastTextClickRef.current;
      const isDoubleClick =
        hit &&
        lastClick.labelId === hit.id &&
        now - lastClick.time < 400;

      lastTextClickRef.current = { time: now, labelId: hit?.id ?? null };

      if (hit && isDoubleClick) {
        activateEnrichPointer();
        textStore.startEditing(hit.id, hit.x, hit.y);
        draw();
        return;
      }

      if (hit) {
        activateEnrichPointer();
        textStore.selectLabel(hit.id);
        textStore.startDrag(wx, wy, hit.x, hit.y);
        draw();
        return;
      }

      textStore.startCreating(wx, wy);
      return;
    }

    if (tool === "map-note" && e.button === 0) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const { wx, wy } = screenToWorld(canvas, cameraRef.current, e.clientX, e.clientY);
      const hex = pixelToHex(wx, wy);
      const key = hexKey(hex);
      const ms = latestRef.current.mapState;
      if (!ms.hexes[key]) return;

      const cam = cameraRef.current;
      const center = hexToPixel(hex);
      const sx = canvas.width / 2 + cam.x + center.x * cam.zoom;
      const sy = canvas.height / 2 + cam.y + center.y * cam.zoom;

      const mapNote = mapNoteByHexKeyRef.current.get(key);
      if (mapNote) {
        startEditingRef.current(mapNote.id, sx, sy);
      } else {
        const offsetX = wx - center.x;
        const offsetY = wy - center.y;
        const mapNoteState = useMapNoteStore.getState();
        const color = mapNoteState.pendingPinColor;
        const hidden = mapNoteState.pendingHidden;
        const templateId = mapNoteState.pendingTemplateId;
        const detail = {
          q: hex.q,
          r: hex.r,
          screenX: sx,
          screenY: sy,
          color,
          hidden,
          offsetX,
          offsetY,
          ...(templateId ? { templateId } : {}),
        };
        document.dispatchEvent(new CustomEvent("map-note-create-click", { detail }));
      }
      return;
    }

    if (tool === "pointer" && e.button === 0) {
      const textStore = useTextLabelStore.getState();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const { wx, wy } = screenToWorld(canvas, cameraRef.current, e.clientX, e.clientY);
      const ms = latestRef.current.mapState;

      if (textStore.selectedLabelId) {
        const selectedLabel = ms.textLabels[textStore.selectedLabelId];
        if (selectedLabel) {
          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          const onHandle = hitTestRotationHandle(ctx, selectedLabel, wx, wy);
          ctx.restore();
          if (onHandle) {
            const handle = getRotationHandleWorldPos(ctx, selectedLabel);
            const startHandleDistance = Math.hypot(
              handle.x - selectedLabel.x,
              handle.y - selectedLabel.y,
            );
            setTextTransformHandleHovered(false);
            textStore.startRotate(
              selectedLabel.rotation ?? 0,
              selectedLabel.fontSize,
              startHandleDistance,
            );
            updateCursor();
            return;
          }
        }
      }

      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      const hit = hitTestTextLabels(ctx, ms.textLabels, wx, wy);
      ctx.restore();

      const now = Date.now();
      const lastClick = lastTextClickRef.current;
      const isDoubleClick =
        hit &&
        lastClick.labelId === hit.id &&
        now - lastClick.time < 400;

      lastTextClickRef.current = { time: now, labelId: hit?.id ?? null };

      if (hit && isDoubleClick) {
        activateEnrichPointer();
        textStore.startEditing(hit.id, hit.x, hit.y);
        draw();
        return;
      }

      if (hit) {
        activateEnrichPointer();
        textStore.selectLabel(hit.id);
        textStore.startDrag(wx, wy, hit.x, hit.y);
        draw();
        return;
      }

      if (textStore.selectedLabelId) {
        setTextTransformHandleHovered(false);
        textStore.clearSelection();
        draw();
      }
      return;
    }

    handlers.handleMouseDown(e);
  }, [handlers, latestRef, draw, activateEnrichPointer, setTextTransformHandleHovered, updateCursor]);

  const handleMouseUpWithText = useCallback((e: React.MouseEvent) => {
    const textStore = useTextLabelStore.getState();

    if (textStore.rotateState && textStore.selectedLabelId) {
      const rs = textStore.rotateState;
      const ms = latestRef.current.mapState;
      const label = ms.textLabels[textStore.selectedLabelId];
      if (
        label &&
        (label.rotation !== rs.startRotation || label.fontSize !== rs.startFontSize)
      ) {
        const before: MapTextLabel = {
          ...label,
          fontSize: rs.startFontSize,
          rotation: rs.startRotation,
        };
        const after = { textLabels: { [label.id]: { ...label } } };
        useEditorStore.getState().pushHistory({
          before: { textLabels: { [label.id]: before } },
          after,
        });
        getBroadcastMapChange()(after);
      }
      textStore.clearRotate();
      updateCursor();
      return;
    }

    if (textStore.dragState && textStore.selectedLabelId) {
      const ds = textStore.dragState;
      const ms = latestRef.current.mapState;
      const label = ms.textLabels[textStore.selectedLabelId];
      if (label && (label.x !== ds.labelStartX || label.y !== ds.labelStartY)) {
        const before: MapTextLabel = {
          ...label,
          x: ds.labelStartX,
          y: ds.labelStartY,
        };
        const after = { textLabels: { [label.id]: { ...label } } };
        useEditorStore.getState().pushHistory({
          before: { textLabels: { [label.id]: before } },
          after,
        });
        getBroadcastMapChange()(after);
      }
      textStore.clearDrag();
      return;
    }
    handlers.handleMouseUp();
  }, [handlers, latestRef, updateCursor]);

  const handleMouseLeaveWithMapNote = useCallback(() => {
    setTextTransformHandleHovered(false);
    handlers.handleMouseLeave();
    scheduleHideRef.current();
  }, [handlers, setTextTransformHandleHovered]);

  const editingLabelId = useTextLabelStore((s) => s.editingLabelId);
  const selectedLabelId = useTextLabelStore((s) => s.selectedLabelId);
  const mapNotePopoverVisible = useMapNoteStore((s) => s.hoveredMapNoteId !== null || s.editingMapNoteId !== null);
  const isMapLocked = editingLabelId !== null || mapNotePopoverVisible;

  const edgeScroll = useEdgeScroll({
    canvasRef,
    cameraRef,
    draw,
    onScrollPaint: handlers.handleEdgeScrollPaint,
    disabled: isMapLocked,
  });

  edgeScrollRef.current = edgeScroll;

  const selectedTileSetId = useEditorStore((s) => s.selectedTileSetId);
  const selectedBrushEntry = useEditorStore((s) => s.selectedBrushEntry);
  const selectedDecorationId = useEditorStore((s) => s.selectedDecorationId);
  const decorationMode = useEditorStore((s) => s.decorationMode);
  const decorationScatterRadius = useEditorStore((s) => s.decorationScatterRadius);
  const decorationScatterDensity = useEditorStore((s) => s.decorationScatterDensity);
  const activeTool = useEditorStore((s) => s.activeTool);
  const editorMode = useEditorStore((s) => s.editorMode);

  useKeyboardNav({
    cameraRef,
    draw,
    onCursorUpdate: updateCursor,
    spaceHeldRef,
    isLocked: isMapLocked,
  });

  useEffect(() => {
    if (!isMapLocked) {
      return;
    }

    spaceHeldRef.current = false;
    edgeScrollRef.current?.stopEdgeScroll();
    updateCursor();
  }, [isMapLocked, updateCursor]);

  useEffect(() => {
    if (activeTool !== "map-note") return;
    const overlayId = MAP_NOTE_PIN_OVERLAY_ID[pendingPinColor];
    const catalog = overlayId ? getCatalogDecorationById(overlayId) : null;
    if (!catalog) return;
    requestDecorationImages([{ id: overlayId, url: catalog.url }], draw);
  }, [activeTool, pendingPinColor, draw]);

  useEffect(() => {
    updateCursor();
    draw();
  }, [
    selectedTileSetId,
    selectedBrushEntry,
    selectedDecorationId,
    decorationMode,
    decorationScatterRadius,
    decorationScatterDensity,
    activeTool,
    editorMode,
    mapNotePinByHexKey,
    pendingPinColor,
    pendingHidden,
    editingLabelId,
    selectedLabelId,
    draw,
    updateCursor,
  ]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function resize() {
      canvas!.width = window.innerWidth;
      canvas!.height = window.innerHeight;
      draw();
    }

    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [draw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function handleWheel(e: WheelEvent) {
      e.preventDefault();
      if (isMapLocked) {
        wheelZoomAccumulatorRef.current = 0;
        return;
      }
      if (e.shiftKey) {
        wheelZoomAccumulatorRef.current = 0;
        const cam = cameraRef.current;
        const horizontalDelta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
        cam.x -= horizontalDelta;
        draw();
        return;
      }
      if (e.metaKey || e.ctrlKey) {
        if (e.deltaY === 0) {
          return;
        }

        wheelZoomAccumulatorRef.current += e.deltaY;
        if (Math.abs(wheelZoomAccumulatorRef.current) < ZOOM_WHEEL_STEP_THRESHOLD) {
          return;
        }

        const direction = wheelZoomAccumulatorRef.current < 0 ? 1 : -1;
        wheelZoomAccumulatorRef.current = 0;
        stepZoomLevel(direction, { sx: e.clientX, sy: e.clientY });
        return;
      }

      wheelZoomAccumulatorRef.current = 0;
      const cam = cameraRef.current;
      cam.x -= e.deltaX;
      cam.y -= e.deltaY;
      draw();
    }

    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleWheel);
  }, [draw, isMapLocked, stepZoomLevel]);

  useEffect(() => {
    if (activeTool !== "text" && activeTool !== "pointer") {
      const ts = useTextLabelStore.getState();
      if (ts.selectedLabelId || ts.editingLabelId) {
        ts.clearSelection();
      }
    }
  }, [activeTool]);

  useEffect(() => {
    if (!selectedLabelId || editingLabelId) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Delete" || e.key === "Backspace") {
        const ms = useMapStore.getState().mapState;
        if (!ms) return;
        const label = ms.textLabels[selectedLabelId!];
        if (!label) return;

        e.preventDefault();
        const deleteAfter = { textLabels: { [label.id]: null as null } };
        useMapStore.getState().applyMapChanges(deleteAfter);
        useEditorStore.getState().pushHistory({
          before: { textLabels: { [label.id]: { ...label } } },
          after: deleteAfter,
        });
        getBroadcastMapChange()(deleteAfter);
        useTextLabelStore.getState().clearSelection();
        draw();
      }
      if (e.key === "Escape") {
        useTextLabelStore.getState().clearSelection();
        draw();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedLabelId, editingLabelId, draw]);

  useEffect(() => {
    draw();
  }, [mapState, draw]);

  useEffect(() => {
    if (!movingMapNoteId) {
      updateCursor();
      return;
    }
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.style.cursor = "crosshair";
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        stopMovingRef.current();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      updateCursor();
    };
  }, [movingMapNoteId, updateCursor]);

  useEffect(() => {
    if (!movingMapNoteId) return;
    draw();
  }, [movingMapNoteId, draw]);

  useEffect(() => {
    return () => {
      savedCamera = { ...cameraRef.current };
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="block"
        onMouseDown={handleMouseDownWithMapNote}
        onMouseMove={handleMouseMoveWithMapNote}
        onMouseUp={handleMouseUpWithText}
        onMouseLeave={handleMouseLeaveWithMapNote}
        onContextMenu={(e) => e.preventDefault()}
      />
      <div className="pointer-events-none fixed left-3 top-16 z-10">
        <div
          className="flex flex-col gap-2"
          aria-label={`Zoom level ${zoomLevelIndex + 1} of ${MAP_ZOOM_LEVELS.length}`}
        >
          {MAP_ZOOM_LEVELS.map((_, index) => (
            <div
              key={index}
              data-testid={`zoom-line-${index + 1}`}
              className={`h-1 w-8 rounded-full bg-white transition-opacity ${
                index === zoomLevelIndex ? "opacity-20" : "opacity-5"
              }`}
            />
          ))}
        </div>
      </div>
      {cursorPreview.visible && !spaceHeldRef.current && activeTool === "eyedropper" && (
        <EyedropperOverlay
          clientX={cursorPreview.clientX}
          clientY={cursorPreview.clientY}
          hoveredTileSetId={cursorPreview.hoveredTileSetId}
        />
      )}
      <MapNotePopover
        campaignId={activeCampaignId}
        canEdit={canEdit}
        mapNoteByHexKey={mapNoteByHexKey}
      />
      <MapNoteMoveBanner />
      <TextLabelEditor cameraRef={cameraRef} canvasRef={canvasRef} />
      <TextStyleToolbar />
      <MapNoteToolPanel />
    </>
  );
}
